const REASONED_DECISIONS = new Set(["REJECTED", "REUPLOAD_REQUIRED"]);
const EDITABLE_MERCHANT_DECISIONS = new Set([
  "APPROVED",
  "REUPLOAD_REQUIRED"
]);
const MERCHANT_GROUPS = [
  ["COMMERCIAL_REGISTER", "commercialRegister"],
  ["TAX_CARD", "taxCard"]
];

const normalizedTranslations = (
  values = {},
  fallback = "",
  language = "en"
) => {
  const result = {
    ar: String(values?.ar || ""),
    en: String(values?.en || "")
  };
  if (!result.ar && !result.en && fallback) {
    result[language === "ar" ? "ar" : "en"] = String(fallback);
  }
  return result;
};

const submissionGroup = (submission, group) =>
  submission?.groups?.find((item) => item?.group === group);

const latestRecoverableGroup = (detail, group) =>
  (detail?.submissionHistory || [])
    .map((submission) => submissionGroup(submission, group))
    .find((item) => EDITABLE_MERCHANT_DECISIONS.has(item?.reviewStatus));

export const buildMerchantReviewGroups = (detail = {}) =>
  MERCHANT_GROUPS.map(([group, metadataKey]) => {
    const metadata = detail.documentMetadata?.[metadataKey] || {};
    const current = submissionGroup(detail.currentSubmission, group);
    const metadataStatus = metadata.status;
    const currentStatus = current?.reviewStatus;
    let source = EDITABLE_MERCHANT_DECISIONS.has(metadataStatus)
      ? metadata
      : EDITABLE_MERCHANT_DECISIONS.has(currentStatus)
        ? current
        : null;

    if (
      !source &&
      (detail.reviewStatus === "REJECTED" || detail.reviewReopenedAt) &&
      !current
    ) {
      source = latestRecoverableGroup(detail, group) || null;
    }

    return {
      group,
      editable:
        detail.reviewStatus === "REJECTED" ||
        Boolean(detail.reviewReopenedAt) ||
        !detail.currentSubmission ||
        Boolean(current),
      reviewStatus: source?.status || source?.reviewStatus || "",
      reasonTranslations: normalizedTranslations(
        source?.reasonTranslations,
        source?.reviewReason || source?.reason
      )
    };
  });

const initialGroupDecision = (group = {}, language = "en") => {
  const currentStatus = EDITABLE_MERCHANT_DECISIONS.has(group.reviewStatus)
    ? group.reviewStatus
    : "";
  return {
    decision: currentStatus,
    reasonTranslations: normalizedTranslations(
      group.reasonTranslations,
      group.reviewReason,
      language
    ),
    reasonLanguage: language === "ar" ? "ar" : "en"
  };
};

export const createReviewForm = ({
  groups = [],
  language = "en",
  overallStatus = "",
  overallReason = "",
  overallReasonTranslations = {}
} = {}) => ({
  decision: "",
  reasonTranslations: { ar: "", en: "" },
  reasonLanguage: language === "ar" ? "ar" : "en",
  globalRejection: {
    selected: overallStatus === "REJECTED",
    wasRejected: overallStatus === "REJECTED",
    reconsidering: false,
    reasonTranslations: normalizedTranslations(
      overallReasonTranslations,
      overallReason,
      language
    ),
    reasonLanguage: language === "ar" ? "ar" : "en"
  },
  groupDecisions: Object.fromEntries(
    groups
      .filter((group) => group?.group && group.editable !== false)
      .map((group) => [
        group.group,
        initialGroupDecision(group, language)
      ])
  ),
  notes: ""
});

const buildDecision = (decision, group, { requireBilingualReason = false } = {}) => {
  const value = String(decision?.decision || "");
  const reasonTranslations = {
    ar: String(decision?.reasonTranslations?.ar || "").trim(),
    en: String(decision?.reasonTranslations?.en || "").trim()
  };
  if (!value) throw new Error("REVIEW_DECISION_REQUIRED");
  const reason = REASONED_DECISIONS.has(value)
    ? reasonTranslations.en || reasonTranslations.ar
    : "";
  if (REASONED_DECISIONS.has(value) && !reason) {
    throw new Error("REVIEW_REASON_REQUIRED");
  }
  if (
    requireBilingualReason &&
    REASONED_DECISIONS.has(value) &&
    (!reasonTranslations.ar || !reasonTranslations.en)
  ) {
    throw new Error("BILINGUAL_REVIEW_REASON_REQUIRED");
  }
  return {
    ...(group ? { group } : {}),
    decision: value,
    ...(reason ? { reason, reasonTranslations } : {})
  };
};

export const buildReviewPayload = (form = {}) => {
  if (form.globalRejection?.selected) {
    return {
      ...buildDecision({
        decision: "REJECTED",
        reasonTranslations: form.globalRejection.reasonTranslations
      }, undefined, { requireBilingualReason: true }),
      notes: String(form.notes || "").trim()
    };
  }

  const groupEntries = Object.entries(form.groupDecisions || {});
  if (groupEntries.length > 0) {
    if (groupEntries.some(([, decision]) => decision?.decision === "REJECTED")) {
      throw new Error("MERCHANT_REJECTION_MUST_APPLY_TO_ALL_GROUPS");
    }
    return {
      decisions: groupEntries.map(([group, decision]) =>
        buildDecision(decision, group, { requireBilingualReason: true })
      ),
      notes: String(form.notes || "").trim()
    };
  }

  return {
    ...buildDecision(form, undefined, { requireBilingualReason: true }),
    notes: String(form.notes || "").trim()
  };
};
